import {RatingStatisticsModel} from "./ratingStatisticsModel";
import {ComparisonTableItemTypeModel} from "./comparisonTableItemTypeModel";
import {ComparisonTableItemType, ComparisonTableItemTypeType} from "../../definitions/types";
import {currencyFormatter} from "../../utils/helpers";

export class ComparisonTableItemModel {
    private readonly _collectionName?: string;
    private readonly _customizeIt: boolean;
    private readonly _designerId?: number;
    private readonly _dimensionalPrice?: number;
    private readonly _imageAlt?: string;
    private readonly _imageSrc: string;
    private readonly _imageTitle?: string;
    private readonly _minQuantity: number;
    private readonly _onSale: boolean;
    private readonly _path: string;
    private readonly _price: number;
    private readonly _primaryId?: number;
    private readonly _productId: number;
    private readonly _ratingStatistics: RatingStatisticsModel;
    private readonly _saleAmount?: number;
    private readonly _salePercentage?: number;
    private readonly _salePrice?: number;
    private readonly _sku: string;
    private readonly _title?: string;
    private readonly _unitPrice: number;
    private readonly _types: ComparisonTableItemTypeModel[];

    constructor(comparisonTableItemType: ComparisonTableItemType) {
        this._collectionName = comparisonTableItemType.collectionName;
        this._customizeIt = comparisonTableItemType.customizeIt;
        this._designerId = comparisonTableItemType.designerId;
        this._dimensionalPrice = comparisonTableItemType.dimensionalPrice;
        this._imageAlt = comparisonTableItemType.imageAlt;
        this._imageSrc = comparisonTableItemType.imageSrc;
        this._imageTitle = comparisonTableItemType.imageTitle;
        this._minQuantity = comparisonTableItemType.minQuantity ?? 1;
        this._onSale = comparisonTableItemType.onSale;
        this._path = comparisonTableItemType.path;
        this._price = comparisonTableItemType.price;
        this._primaryId = comparisonTableItemType.primaryId;
        this._productId = comparisonTableItemType.productId;
        this._ratingStatistics = new RatingStatisticsModel(comparisonTableItemType.ratingStatistics);
        this._saleAmount = comparisonTableItemType.saleAmount;
        this._salePercentage = comparisonTableItemType.salePercentage;
        this._salePrice = comparisonTableItemType.salePrice;
        this._sku = comparisonTableItemType.sku;
        this._title = comparisonTableItemType.title;
        this._unitPrice = comparisonTableItemType.unitPrice;
        this._types = comparisonTableItemType.types
            .filter((type): type is ComparisonTableItemTypeType => type !== null)
            .map((type) => new ComparisonTableItemTypeModel(type));
    }

    /**
     * @property collectionName
     * @description The name of the collection.
     * @returns {string | undefined}
     */
    public get collectionName(): string | undefined {
        return this._collectionName;
    }

    /**
     * @property customizeIt
     * @description Whether the product can be customized.
     * @returns {boolean}
     */
    public get customizeIt(): boolean {
        return this._customizeIt;
    }

    /**
     * @property designerId
     * @description The unique identifier of the designer.
     * @returns {number | undefined}
     */
    public get designerId(): number | undefined {
        return this._designerId;
    }

    /**
     * @property dimensionalPrice
     * @description The dimensional price of the product.
     * @returns {number | undefined}
     */
    public get dimensionalPrice(): number | undefined {
        return this._dimensionalPrice;
    }

    /**
     * @property imageAlt
     * @description The alt text of the product image.
     * @returns {string | undefined}
     */
    public get imageAlt(): string | undefined {
        return this._imageAlt;
    }

    /**
     * @property imageSrc
     * @description The source of the product image.
     * @returns {string}
     */
    public get imageSrc(): string {
        return this._imageSrc;
    }

    /**
     * @property imageTitle
     * @description The title of the product image.
     * @returns {string | undefined}
     */
    public get imageTitle(): string | undefined {
        return this._imageTitle;
    }

    /**
     * @property isDynamicDimension
     * @description Whether the product has a dynamic dimension.
     * @returns {boolean}
     */
    public get isDynamicDimension(): boolean {
        return !!(this._dimensionalPrice && this._dimensionalPrice > 0);
    }

    /**
     * @property minQuantity
     * @description The minimum quantity of the product.
     * @returns {number}
     */
    public get minQuantity(): number {
        return this._minQuantity;
    }

    /**
     * @property onSale
     * @description Whether the product is on sale.
     * @returns {boolean}
     */
    public get onSale(): boolean {
        return this._onSale;
    }

    /**
     * @property path
     * @description The path of the product.
     * @returns {string}
     */
    public get path(): string {
        return this._path;
    }

    /**
     * @property href
     * @description The path of the product.
     * @returns {string}
     */
    public get href(): string {
        return this._customizeIt ? this._path : `/shoppingcart/add?productid=${this._productId}&quantity=${this._minQuantity}`;
    }

    /**
     * @property price
     * @description The price of the product.
     * @returns {number}
     */
    public get price(): number {
        return this._price;
    }

    /**
     * @property primaryId
     * @description The primary identifier of the product.
     * @returns {number | undefined}
     */
    public get primaryId(): number | undefined {
        return this._primaryId;
    }

    /**
     * @property productId
     * @description The unique identifier of the product.
     * @returns {number}
     */
    public get productId(): number {
        return this._productId;
    }

    /**
     * @property ratingStatistics
     * @description The rating statistics of the product.
     * @returns {RatingStatisticsModel}
     */
    public get ratingStatistics(): RatingStatisticsModel {
        return this._ratingStatistics;
    }

    /**
     * @property saleAmount
     * @description The sale amount of the product.
     * @returns {number | undefined}
     */
    public get saleAmount(): number | undefined {
        return this._saleAmount;
    }

    /**
     * @property salePercentage
     * @description The sale percentage of the product.
     * @returns {number | undefined}
     */
    public get salePercentage(): number | undefined {
        return this._salePercentage;
    }

    /**
     * @property salePrice
     * @description The sale price of the product.
     * @returns {number | undefined}
     */
    public get salePrice(): number | undefined {
        return this._salePrice;
    }

    /**
     * @property sku
     * @description The SKU of the product.
     * @returns {string}
     */
    public get sku(): string {
        return this._sku;
    }

    /**
     * @property title
     * @description The title of the product.
     * @returns {string | undefined}
     */
    public get title(): string | undefined {
        return this._title;
    }

    /**
     * @method truncatedTitle
     * @description The truncated title of the product.
     * @returns {string | undefined}
     */
    public get truncatedTitle(): string | undefined {
        if (!this._title) return undefined;
        return this._title?.length > 60 ? `${this._title.substring(0, 60)}...` : this._title;
    }

    /**
     * @property unitPrice
     * @description The unit price of the product.
     * @returns {number}
     */
    public get unitPrice(): number {
        return this._unitPrice;
    }

    /**
     * @property types
     * @description The types of the product.
     * @returns {ComparisonTableItemTypeModel[]}
     */
    public get types(): ComparisonTableItemTypeModel[] {
        return this._types;
    }

    /**
     * @method getSpecificationValue
     * @description Gets the value of the specification type.
     * @param {number} typeId The unique identifier of the specification type.
     * @returns {string | undefined}
     */
    public getSpecificationValue(typeId: number): string | undefined {
        const type = this._types.find((type) => type.typeId === typeId);
        return type?.value;
    }

    /**
     * @property isCurrentItem
     * @description Whether the item is the current item.
     * @param {number} productId
     * @return {boolean}
     */
    public isCurrentItem(productId: number): boolean {
        return this._productId === productId;
    }

    /**
     * @method getTypeValue
     * @description Gets the value of the type by the unique identifier ('---' if not found).
     * @param {number} typeId The unique identifier of the type.
     * @returns {string}
     */
    public getTypeValue(typeId: number): string {
        const type = this._types.find((type) => type.typeId === typeId);
        return type?.value ?? "---";
    }

    /**
     * @method formatted
     * @description Returns the current price, unit price, sale amount, and sale percentage formatted as currency strings
     * @returns {{currentPrice: string, unitPrice: string, saleAmount: string | undefined, salePercentage: string | undefined}}
     * @example
     * {
     *    price: "$20.00",
     *    unitPrice: "$20.00",
     *    saleAmount: "$5.00",
     *    salePercentage: "(20%)"
     * }
     */
    public get formatted(): {
        price: string;
        unitPrice: string;
        saleAmount?: string;
        salePercentage?: string;
    } {
        return {
            price: currencyFormatter.format(this.price),
            unitPrice: currencyFormatter.format(this.unitPrice),
            saleAmount: this.saleAmount ? currencyFormatter.format(this.saleAmount) : undefined,
            salePercentage: this.salePercentage ? currencyFormatter.format(this.salePercentage) : undefined
        }
    }
}